import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';
import { useQuery } from '@tanstack/react-query';

import { useMoveAccountMutation } from '#accounts';
import { buildRateIndex } from '#accounts/currency';
import { isAccountFailedSync } from '#accounts/syncStatus';
import { exchangeRateQueries } from '#exchange-rates';
import { useAccounts } from '#hooks/useAccounts';
import { useClosedAccounts } from '#hooks/useClosedAccounts';
import { useLocalPref } from '#hooks/useLocalPref';
import { useOffBudgetAccounts } from '#hooks/useOffBudgetAccounts';
import { useOnBudgetAccounts } from '#hooks/useOnBudgetAccounts';
import { useUpdatedAccounts } from '#hooks/useUpdatedAccounts';
import { useSelector } from '#redux';
import * as bindings from '#spreadsheet/bindings';

import { Account } from './Account';
import { useGroupBalanceValue } from './GroupBalance';
import { SecondaryItem } from './SecondaryItem';

const fontWeight = 600;

export function Accounts() {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const { data: accounts = [] } = useAccounts();
  const updatedAccounts = useUpdatedAccounts();
  const { data: offbudgetAccounts = [] } = useOffBudgetAccounts();
  const { data: onBudgetAccounts = [] } = useOnBudgetAccounts();
  const { data: closedAccounts = [] } = useClosedAccounts();
  const syncingAccountIds = useSelector(state => state.account.accountsSyncing);

  const getAccountPath = (account: AccountEntity) => `/accounts/${account.id}`;

  const [showClosedAccounts, setShowClosedAccountsPref] = useLocalPref(
    'ui.showClosedAccounts',
  );

  function onDragChange(drag: { state: string }) {
    setIsDragging(drag.state === 'start');
  }

  const moveAccount = useMoveAccountMutation();

  const makeDropPadding = (i: number) => {
    if (i === 0) {
      return {
        paddingTop: isDragging ? 15 : 0,
        marginTop: isDragging ? -15 : 0,
      };
    }
    return undefined;
  };

  async function onReorder(
    id: string,
    dropPos: 'top' | 'bottom' | null,
    targetId: string,
  ) {
    let targetIdToMove: string | null = targetId;
    if (dropPos === 'bottom') {
      const idx = accounts.findIndex(a => a.id === targetId) + 1;
      targetIdToMove = idx < accounts.length ? accounts[idx].id : null;
    }

    moveAccount.mutate({ id, targetId: targetIdToMove });
  }

  const onToggleClosedAccounts = () => {
    setShowClosedAccountsPref(!showClosedAccounts);
  };

  // Built once and shared by every group total below and every per-account
  // row's own foreign-currency display, instead of each independently
  // re-fetching/re-sorting the same rates array.
  const { data: rates = [] } = useQuery(exchangeRateQueries.list());
  const rateIndex = useMemo(() => buildRateIndex(rates), [rates]);

  // Group totals need per-account currency conversion, which a single
  // cross-account SQL $sum (the allAccountBalance/onBudgetAccountBalance/
  // offBudgetAccountBalance bindings) can't do -- see GroupBalance.tsx.
  // onBudgetAccounts + offbudgetAccounts covers exactly the same accounts
  // as allAccountBalance's own filter (all non-closed accounts).
  const allNonClosedAccounts = [...onBudgetAccounts, ...offbudgetAccounts];
  const allBalance = useGroupBalanceValue(allNonClosedAccounts, rateIndex);
  const onBudgetBalance = useGroupBalanceValue(onBudgetAccounts, rateIndex);
  const offBudgetBalance = useGroupBalanceValue(offbudgetAccounts, rateIndex);

  return (
    <View
      style={{
        flexGrow: 1,
        '@media screen and (max-height: 480px)': {
          minHeight: 'auto',
        },
      }}
    >
      <View
        style={{
          height: 1,
          backgroundColor: theme.sidebarItemBackgroundHover,
          marginTop: 15,
          flexShrink: 0,
        }}
      />

      <View style={{ overflow: 'auto' }}>
        {allBalance.subscribers}
        <Account
          name={t('All accounts')}
          to="/accounts"
          value={allBalance.total}
          style={{ fontWeight, marginTop: 15 }}
          isExactPathMatch
          balanceTestId="sidebar-all-accounts-balance"
        />

        {onBudgetAccounts.length > 0 && (
          <>
            {onBudgetBalance.subscribers}
            <Account
              name={t('On budget')}
              to="/accounts/onbudget"
              value={onBudgetBalance.total}
              style={{
                fontWeight,
                marginTop: 13,
                marginBottom: 5,
              }}
              titleAccount
              balanceTestId="sidebar-on-budget-balance"
            />
          </>
        )}

        {onBudgetAccounts.map((account, i) => (
          <Account
            key={account.id}
            name={account.name}
            account={account}
            connected={!!account.bank}
            pending={syncingAccountIds.includes(account.id)}
            failed={isAccountFailedSync(account)}
            updated={updatedAccounts.includes(account.id)}
            to={getAccountPath(account)}
            query={bindings.accountBalance(account.id)}
            rateIndex={rateIndex}
            onDragChange={onDragChange}
            onDrop={onReorder}
            outerStyle={makeDropPadding(i)}
          />
        ))}

        {offbudgetAccounts.length > 0 && (
          <>
            {offBudgetBalance.subscribers}
            <Account
              name={t('Off budget')}
              to="/accounts/offbudget"
              value={offBudgetBalance.total}
              style={{
                fontWeight,
                marginTop: 13,
                marginBottom: 5,
              }}
              titleAccount
              balanceTestId="sidebar-off-budget-balance"
            />
          </>
        )}

        {offbudgetAccounts.map((account, i) => (
          <Account
            key={account.id}
            name={account.name}
            account={account}
            connected={!!account.bank}
            pending={syncingAccountIds.includes(account.id)}
            failed={isAccountFailedSync(account)}
            updated={updatedAccounts.includes(account.id)}
            to={getAccountPath(account)}
            query={bindings.accountBalance(account.id)}
            rateIndex={rateIndex}
            onDragChange={onDragChange}
            onDrop={onReorder}
            outerStyle={makeDropPadding(i)}
          />
        ))}

        {closedAccounts.length > 0 && (
          <SecondaryItem
            style={{ marginTop: 15 }}
            title={
              showClosedAccounts
                ? t('Closed accounts')
                : t('Closed accounts...')
            }
            onClick={onToggleClosedAccounts}
            bold
          />
        )}

        {showClosedAccounts &&
          closedAccounts.map(account => (
            <Account
              key={account.id}
              name={account.name}
              account={account}
              to={getAccountPath(account)}
              query={bindings.accountBalance(account.id)}
              rateIndex={rateIndex}
              onDragChange={onDragChange}
              onDrop={onReorder}
            />
          ))}
      </View>
    </View>
  );
}
